import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Loader2, Send, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { SendReplyShortcutHint } from '@/components/common/SendReplyShortcutHint'
import { useSendReplyShortcut } from '@/hooks/useSendReplyShortcut'
import { toast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api'
import { cn } from '@/lib/utils'
import { PLATFORM_LABELS } from '@/types/brandMentions'
import { useProgramContext } from '@/context/ProgramContext'
import { useTeamContext } from '@/context/TeamContext'
import type { ChatMessage } from '@/types/chat.types'
import type { HeroInboxItem, InboxKind } from '@/components/landing/HeroInboxPreview'

type FeedbackConversation = {
	id: string
	sender_type: 'support' | 'customer'
	sender_name: string | null
	sender_email: string | null
	message: string
	created_at: string
}

type ThreadEntry = {
	id: string
	from: string
	at: string
	body: string
	team: boolean
}

function canReplyToKind(kind: InboxKind) {
	return kind === 'bug' || kind === 'feature' || kind === 'contact' || kind === 'chat'
}

function aiFeedbackType(kind: InboxKind) {
	if (kind === 'bug') return 'bugs'
	if (kind === 'feature') return 'features'
	if (kind === 'contact') return 'contacts'
	return null
}

function threadEndpoint(item: HeroInboxItem) {
	if (!item.entityId) return null
	if (item.kind === 'bug') return `/api/bugs/protected/${item.entityId}/conversations`
	if (item.kind === 'feature') return `/api/features/protected/${item.entityId}/conversations`
	if (item.kind === 'contact') return `/api/contacts/protected/${item.entityId}/conversations`
	if (item.kind === 'chat') return `/api/chat/conversations/${item.entityId}/messages`
	return null
}

function replyEndpoint(item: HeroInboxItem) {
	if (!item.entityId) return null
	if (item.kind === 'bug') return `/api/bugs/protected/${item.entityId}/reply`
	if (item.kind === 'feature') return `/api/features/protected/${item.entityId}/reply`
	if (item.kind === 'contact') return `/api/contacts/protected/${item.entityId}/reply`
	if (item.kind === 'chat') return `/api/chat/conversations/${item.entityId}/messages`
	return null
}

function formatInboxDateTime(iso: string, isFrench: boolean) {
	return new Date(iso).toLocaleString(isFrench ? 'fr-FR' : 'en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function ThreadBubble({
	from,
	at,
	body,
	isFrench,
	team = false,
}: {
	from: string
	at: string
	body: string
	isFrench: boolean
	team?: boolean
}) {
	return (
		<div className={cn('fb-inbox-thread-msg', team && 'is-team')}>
			<div>
				<strong>{from}</strong>
				<time dateTime={at}>{formatInboxDateTime(at, isFrench)}</time>
			</div>
			<p>{body}</p>
		</div>
	)
}

function useInboxLiveThread(item: HeroInboxItem) {
	const { t } = useTranslation()
	const endpoint = threadEndpoint(item)

	return useQuery({
		queryKey: ['inbox-live-thread', item.kind, item.entityId],
		enabled: Boolean(endpoint),
		queryFn: async (): Promise<ThreadEntry[]> => {
			if (!endpoint) return []
			if (item.kind === 'chat') {
				const data = await apiClient<{ messages: ChatMessage[] }>(endpoint)
				return (data.messages || [])
					.filter((message) => Boolean(message.content?.trim()))
					.map((message) => ({
						id: message.id,
						from:
							message.sender_type === 'agent'
								? t('landing.heroInbox.support')
								: item.author,
						at: message.created_at,
						body: message.content || '',
						team: message.sender_type === 'agent',
					}))
			}
			const data = await apiClient<{ conversations: FeedbackConversation[] }>(endpoint)
			return (data.conversations || []).map((conversation) => ({
				id: conversation.id,
				from:
					conversation.sender_name ||
					conversation.sender_email ||
					(conversation.sender_type === 'support'
						? t('landing.heroInbox.support')
						: item.author),
				at: conversation.created_at,
				body: conversation.message,
				team: conversation.sender_type === 'support',
			}))
		},
	})
}

export function InboxLiveThread({
	item,
	isFrench,
}: {
	item: HeroInboxItem
	isFrench: boolean
}) {
	const { data: messages = [], isLoading } = useInboxLiveThread(item)
	if (!canReplyToKind(item.kind)) return null
	if (isLoading) {
		return <div className="h-16 animate-pulse rounded-lg bg-muted" />
	}
	if (messages.length === 0) return null
	return (
		<>
			{messages.map((message) => (
				<ThreadBubble
					key={message.id}
					from={message.from}
					at={message.at}
					body={message.body}
					isFrench={isFrench}
					team={message.team}
				/>
			))}
		</>
	)
}

export function InboxLiveComposer({
	item,
	canWrite,
}: {
	item: HeroInboxItem
	canWrite: boolean
}) {
	const { t } = useTranslation()
	const { programId } = useProgramContext()
	const { teamId } = useTeamContext()
	const queryClient = useQueryClient()
	const [replyText, setReplyText] = useState('')
	const [isGeneratingDraft, setIsGeneratingDraft] = useState(false)
	const [appliedAiDraft, setAppliedAiDraft] = useState<string | null>(null)
	const [aiSuggestionEventId, setAiSuggestionEventId] = useState<string | null>(null)
	const hasEmail = Boolean(item.context?.email)
	const isChat = item.kind === 'chat'
	const isMention = item.kind === 'mention'
	const chatClosed = isChat && item.status === 'closed'
	const canUseComposer = canReplyToKind(item.kind) && (isChat || hasEmail)
	const endpoint = replyEndpoint(item)
	const draftType = aiFeedbackType(item.kind)

	const sendMutation = useMutation({
		mutationFn: async (message: string) => {
			if (!endpoint) throw new Error('Missing reply endpoint')
			if (isChat) {
				return apiClient(endpoint, {
					method: 'POST',
					data: { content: message },
				})
			}
			return apiClient(endpoint, {
				method: 'POST',
				data: {
					message,
					aiSuggestionEventId: appliedAiDraft ? aiSuggestionEventId : null,
					aiEdited: appliedAiDraft ? message !== appliedAiDraft : false,
				},
			})
		},
		onSuccess: () => {
			setReplyText('')
			setAppliedAiDraft(null)
			setAiSuggestionEventId(null)
			queryClient.invalidateQueries({ queryKey: ['inbox-live-thread', item.kind, item.entityId] })
			if (isChat) {
				queryClient.invalidateQueries({ queryKey: ['messages', item.entityId] })
				queryClient.invalidateQueries({ queryKey: ['conversations'] })
			}
		},
		onError: (error) => {
			toast({
				title: t('chatWindow.error'),
				description:
					error instanceof Error ? error.message : t('chatWindow.failedToSendMessage'),
				variant: 'destructive',
			})
		},
	})

	const canSend =
		canWrite &&
		canUseComposer &&
		!chatClosed &&
		!!replyText.trim() &&
		!sendMutation.isPending

	const sendReply = useCallback(() => {
		const message = replyText.trim()
		if (!message || !canSend) return
		sendMutation.mutate(message)
	}, [canSend, replyText, sendMutation])

	const generateDraft = useCallback(async () => {
		if (!item.entityId || !draftType || isGeneratingDraft) return
		setIsGeneratingDraft(true)
		try {
			const data = await apiClient<{
				suggestion?: { draft?: string }
				eventId?: string | null
			}>(`/api/ai-assistant/${draftType}/${item.entityId}/suggest-reply`, {
				method: 'POST',
				data: {},
			})
			const draft = data.suggestion?.draft?.trim()
			if (!draft) throw new Error(t('chatWindow.failedToSendMessage'))
			setReplyText(draft)
			setAppliedAiDraft(draft)
			setAiSuggestionEventId(data.eventId || null)
			queryClient.invalidateQueries({ queryKey: ['ai-assistant-status', teamId, programId] })
		} catch (error) {
			toast({
				title: t('chatWindow.error'),
				description:
					error instanceof Error
						? error.message
						: t(
								'contactMessageDetailView.aiDraftErrorSimple',
								'Could not generate a draft right now. Please try again.'
							),
				variant: 'destructive',
			})
		} finally {
			setIsGeneratingDraft(false)
		}
	}, [draftType, isGeneratingDraft, item.entityId, programId, queryClient, t, teamId])

	const { onKeyDown, shortcutLabel } = useSendReplyShortcut(sendReply, {
		canSend,
		isSending: sendMutation.isPending,
	})

	if (isMention && item.href) {
		const platform =
			PLATFORM_LABELS[item.source as keyof typeof PLATFORM_LABELS] || item.meta || item.source
		return (
			<footer className="flex-shrink-0 border-t bg-background/95 p-3">
				<Button asChild variant="outline" size="sm" className="h-8 gap-1.5">
					<a href={item.href} target="_blank" rel="noopener noreferrer">
						<ExternalLink className="h-3.5 w-3.5" />
						{t('landing.heroInbox.replyOnPost', { platform })}
					</a>
				</Button>
			</footer>
		)
	}

	if (!canReplyToKind(item.kind)) return null

	if (!isChat && !hasEmail) {
		return (
			<footer className="flex-shrink-0 border-t bg-background/95 p-3">
				<p className="text-xs text-muted-foreground">{t('landing.heroInbox.cannotReplyNoEmail')}</p>
			</footer>
		)
	}

	if (chatClosed) {
		return (
			<footer className="flex-shrink-0 border-t bg-background/95 p-3">
				<p className="text-xs text-muted-foreground">{t('chatWindow.conversationIsClosed')}</p>
			</footer>
		)
	}

	if (!canWrite) return null

	return (
		<footer className="flex-shrink-0 border-t bg-background/95 p-3">
			<div className="space-y-2">
				<Textarea
					value={replyText}
					onChange={(event) => setReplyText(event.target.value)}
					onKeyDown={onKeyDown}
					aria-label={t('landing.heroInbox.replyPlaceholder')}
					placeholder={t('landing.heroInbox.replyPlaceholder')}
					className="min-h-[96px] resize-y text-sm"
					rows={4}
					disabled={sendMutation.isPending || isGeneratingDraft}
				/>
				<div className="flex items-center justify-between gap-2">
					{draftType ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="h-8 gap-1.5"
							onClick={generateDraft}
							disabled={isGeneratingDraft || sendMutation.isPending}
						>
							{isGeneratingDraft ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<Sparkles className="h-3.5 w-3.5" />
							)}
							{t('landing.heroInbox.generateDraft')}
						</Button>
					) : (
						<span />
					)}
					<Button
						size="sm"
						className="h-8 gap-1.5 px-3"
						disabled={!canSend}
						onClick={sendReply}
					>
						{sendMutation.isPending ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
						) : (
							<Send className="h-3.5 w-3.5" />
						)}
						{sendMutation.isPending
							? t('bugReportDetailView.sending')
							: t('landing.heroInbox.send')}
						{!sendMutation.isPending && <SendReplyShortcutHint label={shortcutLabel} />}
					</Button>
				</div>
			</div>
		</footer>
	)
}
